import React, { useState } from 'react';
import { 
  Laptop, 
  Smartphone, 
  Tablet, 
  ChevronUp, 
  ChevronDown, 
  Trash2, 
  Plus, 
  Layers, 
  FileText, 
  ShieldCheck, 
  HelpCircle,
  ExternalLink
} from 'lucide-react';
import { CustomProject, ProjectPage } from '../../types/productBuilder';

interface SandboxProps {
  project: CustomProject;
  onUpdateProject: (p: CustomProject) => void;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

export const Sandbox: React.FC<SandboxProps> = ({ project, onUpdateProject, onAddLog }) => {
  const [viewportWidth, setViewportWidth] = useState<'100%' | '768px' | '375px'>('100%');
  const [activePageId, setActivePageId] = useState<string>(project.pages[0]?.id || '');
  const [newSectionName, setNewSectionName] = useState('');
  const [newPageTitle, setNewPageTitle] = useState('');

  const activePage = project.pages.find(p => p.id === activePageId) || project.pages[0];

  const moveSection = (index: number, direction: 'UP' | 'DOWN') => {
    if (!activePage) return;
    const items = [...activePage.layoutSections];
    const target = direction === 'UP' ? index - 1 : index + 1;
    if (target < 0 || target >= items.length) return;

    const temp = items[index];
    items[index] = items[target];
    items[target] = temp;

    const updatedPages = project.pages.map(p => 
      p.id === activePage.id ? { ...p, layoutSections: items } : p
    );

    onUpdateProject({ ...project, pages: updatedPages });
    onAddLog('Product Builder', `Reordered layout rows for "${activePage.title}"`, 'SUCCESS', 'Section order synchronized.');
  };

  const removeSection = (index: number) => {
    if (!activePage) return;
    const items = activePage.layoutSections.filter((_, idx) => idx !== index);
    const updatedPages = project.pages.map(p => 
      p.id === activePage.id ? { ...p, layoutSections: items } : p
    );

    onUpdateProject({ ...project, pages: updatedPages });
    onAddLog('Product Builder', `Removed component section from "${activePage.title}"`, 'WARNING', 'Section list trimmed.');
  };

  const addCustomSection = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSectionName.trim() || !activePage) return;

    const items = [...activePage.layoutSections, newSectionName.trim()];
    const updatedPages = project.pages.map(p => 
      p.id === activePage.id ? { ...p, layoutSections: items } : p
    );

    onUpdateProject({ ...project, pages: updatedPages });
    onAddLog('Product Builder', `Appended new section row "${newSectionName.trim()}" to "${activePage.title}"`, 'SUCCESS', 'Custom component registered.');
    setNewSectionName('');
  };

  const addNewPage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPageTitle.trim()) return;

    const newPage: ProjectPage = {
      id: 'page_' + Date.now(),
      title: newPageTitle.trim(),
      url: '/' + newPageTitle.trim().toLowerCase().replace(/[^a-z0-9]/g, '-'),
      layoutSections: ['Atmospheric Header', 'Metrics Analytics Section Row', 'Direct Intake Message Gate']
    };

    onUpdateProject({ ...project, pages: [...project.pages, newPage] });
    onAddLog('Product Builder', `Appended new page layout "${newPageTitle.trim()}"`, 'SUCCESS', 'Prerelease scaffolding registered.');
    setActivePageId(newPage.id);
    setNewPageTitle('');
  };

  const removePage = (id: string, name: string) => {
    if (project.pages.length <= 1) {
      alert("Projects must retain at least one core page frame.");
      return;
    }
    const updated = project.pages.filter(p => p.id !== id);
    onUpdateProject({ ...project, pages: updated });
    onAddLog('Product Builder', `Wiped page layout frame "${name}"`, 'WARNING', 'Modified project pages length.');
    if (activePageId === id) {
      setActivePageId(updated[0].id);
    }
  };

  if (!activePage) {
    return (
      <div className="text-center font-mono py-12 text-zinc-500">
        <span>No project loaded. Initialize a template first.</span>
      </div>
    );
  }

  // Define brand colors based on selected swatch
  const primaryColor = project.colors?.primary || '#00ffff';
  const darkBackgroundColor = project.colors?.darkAccent || '#010206';
  const fontClass = project.typography === 'Space Grotesk' ? 'font-sans tracking-tight' :
                    project.typography === 'JetBrains Mono' ? 'font-mono text-xs' :
                    project.typography === 'Outfit' ? 'font-sans font-light tracking-wide' :
                    project.typography === 'Playfair Display' ? 'font-serif italic' : 'font-sans';

  const generatePreviewHtml = () => {
    const primary = project.colors?.primary || '#00ffff';
    const darkAccent = project.colors?.darkAccent || '#010206';
    const textPrimary = project.colors?.textPrimary || '#ffffff';
    
    const sectionsHtml = activePage.layoutSections.map((sec, idx) => `
      <div 
        class="p-4 rounded-xl border border-white/5 flex items-center justify-between transition-all"
        style="background: rgba(0, 0, 0, 0.4); border-left: 3px solid ${primary};"
      >
        <div class="space-y-1">
          <span class="text-xs font-mono font-bold tracking-wider text-white uppercase block">▣ ${sec}</span>
          <span class="text-[9.5px] text-zinc-400 font-sans block leading-normal">
            Isolated components sandbox. Fully compliant with direct zero-trust data constraints.
          </span>
        </div>
        <span class="text-[8px] font-mono text-zinc-500 bg-black/60 border border-white/5 px-2 py-0.5 rounded uppercase">
          BLOCK_${idx+1}
        </span>
      </div>
    `).join('\n');

    const fontStyle = project.typography === 'Space Grotesk' ? "font-family: 'Space Grotesk', sans-serif;" :
                      project.typography === 'JetBrains Mono' ? "font-family: 'JetBrains Mono', monospace; font-size: 12px;" :
                      project.typography === 'Outfit' ? "font-family: 'Outfit', sans-serif;" :
                      project.typography === 'Playfair Display' ? "font-family: 'Playfair Display', serif;" : "font-family: 'Inter', sans-serif;";

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <script src="https://cdn.tailwindcss.com"></script>
          <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600;700&family=JetBrains+Mono:wght@400;700&family=Outfit:wght@300;450;700&family=Playfair+Display:ital,wght@0,400;1,400&family=Inter:wght@400;500;700&display=swap" rel="stylesheet">
          <style>
            body {
              ${fontStyle}
              background-color: ${darkAccent};
              color: ${textPrimary};
            }
            /* Custom minimalist scrollbar */
            ::-webkit-scrollbar {
              width: 5px;
            }
            ::-webkit-scrollbar-track {
              background: transparent;
            }
            ::-webkit-scrollbar-thumb {
              background: rgba(255, 255, 255, 0.1);
              border-radius: 9px;
            }
          </style>
        </head>
        <body class="p-6 min-h-screen flex flex-col justify-between select-none">
          <div class="space-y-6">
            <div class="space-y-1.5 text-left">
              <span class="text-[8px] font-mono uppercase px-2 py-0.5 rounded border inline-block" style="color: ${primary}; border-color: ${primary}30; background-color: ${primary}05;">
                INDUSTRY VERTICAL: ${project.industry.toUpperCase()}
              </span>
              <h2 class="text-lg md:text-xl font-black uppercase text-white tracking-tight">${activePage.title}</h2>
              <p class="text-[10px] text-zinc-400 leading-normal font-sans">
                Rendered inside high-fidelity client sandbox mode. Standard cookies, page secrets, and high-privilege parent APIs are strictly blocked by origin sandbox controls.
              </p>
            </div>

            <div class="grid grid-cols-1 gap-3 pt-2">
              ${sectionsHtml}
            </div>
          </div>

          <div class="border-t border-white/5 pt-3 mt-6 flex justify-between items-center text-[7.5px] font-mono text-zinc-550 uppercase tracking-wider">
            <span>Ref: PX-${project.id.substring(0, 8)}</span>
            <span class="flex items-center gap-1" style="color: ${primary}cc;">
              ● Isolated Sandbox Preview (Active)
            </span>
          </div>
        </body>
      </html>
    `;
  };

  return (
    <div className="npx-builder-sandbox-container grid grid-cols-1 lg:grid-cols-12 gap-6 text-left items-start animate-fade-in">
      
      {/* LEFT: LIVE SCREEN WORKSPACE SIMULATOR */}
      <div className="lg:col-span-8 space-y-4">
        
        {/* VIEWPORT CONTROLS */}
        <div className="flex flex-wrap items-center justify-between bg-zinc-950/60 p-3 rounded-2xl border border-white/5 gap-3">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-[#00ffff] rounded-full animate-pulse"></span>
            <span className="text-[10px] font-mono font-bold text-zinc-400 uppercase tracking-widest">Preview Sandbox: client@nexus</span>
          </div>

          <div className="flex items-center gap-1.5 bg-black/60 p-1 rounded-xl border border-white/5">
            <button
              onClick={() => setViewportWidth('100%')}
              className={`px-3 py-1 font-mono text-[9px] uppercase font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1 ${viewportWidth === '100%' ? 'bg-[#00ffff]/15 text-[#00ffff]' : 'text-zinc-500 hover:text-white'}`}
            >
              <Laptop size={11} />
              <span>Full Desktop</span>
            </button>
            <button
              onClick={() => setViewportWidth('768px')}
              className={`px-3 py-1 font-mono text-[9px] uppercase font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1 ${viewportWidth === '768px' ? 'bg-[#00ffff]/15 text-[#00ffff]' : 'text-zinc-500 hover:text-white'}`}
            >
              <Tablet size={11} />
              <span>Tablet</span>
            </button>
            <button
              onClick={() => setViewportWidth('375px')}
              className={`px-3 py-1 font-mono text-[9px] uppercase font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1 ${viewportWidth === '375px' ? 'bg-[#00ffff]/15 text-[#00ffff]' : 'text-zinc-500 hover:text-white'}`}
            >
              <Smartphone size={11} />
              <span>Mobile VP</span>
            </button>
          </div>
        </div>

        {/* DEVICE CONTAINER */}
        <div className="p-3 bg-black/60 rounded-3xl border border-white/5 flex justify-center">
          <div
            className="rounded-2xl border min-h-[500px] overflow-hidden flex flex-col justify-between transition-all duration-300 relative shadow-inner"
            style={{
              width: viewportWidth,
              backgroundColor: darkBackgroundColor,
              borderColor: `${primaryColor}22`,
              boxShadow: `0 0 40px ${primaryColor}10`
            }}
          >
            {/* Header frame */}
            <div className="flex items-center justify-between border-b border-white/10 pb-3 p-6 pb-3 font-mono">
              <span className="text-xs font-black tracking-tighter" style={{ color: primaryColor }}>
                ◆ {project.name.toUpperCase().substring(0, 18)}
              </span>

              <div className="flex items-center gap-2 text-[8px] uppercase font-bold text-zinc-400">
                {project.pages.map((p) => (
                  <span
                    key={p.id}
                    onClick={() => setActivePageId(p.id)}
                    className={`cursor-pointer px-1.5 py-0.5 rounded transition-all ${p.id === activePage.id ? 'bg-white/10 text-white border border-white/20' : 'hover:text-white'}`}
                  >
                    {p.title}
                  </span>
                ))}
              </div>
            </div>

            {/* Inner Content body via sandboxed iframe isolated from cookies or parent credentials */}
            <div className="flex-grow w-full relative">
              <iframe
                title="Galaxy Sandbox Preview"
                sandbox="allow-scripts"
                srcDoc={generatePreviewHtml()}
                className="w-full h-[370px] bg-transparent border-0"
              />
            </div>

            {/* Simulated legal disclaimer */}
            <div className="border-t border-white/5 pt-2 flex justify-between items-center text-[7px] font-mono text-zinc-650 uppercase">
              <span>Sandbox Ref: PX-{project.id.substring(0,8)}</span>
              <span className="flex items-center gap-1 text-[#00ffff]/60 font-black">
                <ShieldCheck size={9} />
                <span>Isolated Draft Mode (Safe)</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT: LAYOUT & SECTION MANAGER */}
      <div className="lg:col-span-4 bg-zinc-950/40 p-4 rounded-2xl border border-white/5 space-y-5">
        
        {/* PAGES MANAGER */}
        <div className="space-y-3">
          <div className="flex items-center gap-1 text-[10px] font-mono text-zinc-400 uppercase font-bold border-b border-white/5 pb-2">
            <FileText size={12} className="text-purple-400" />
            <span>Pages Outline</span>
          </div>

          <div className="space-y-1">
            {project.pages.map((p) => (
              <div
                key={p.id}
                className={`p-2 rounded-xl flex items-center justify-between text-[9.5px] transition-all border ${p.id === activePage.id ? 'bg-zinc-900 border-[#00ffff]/20 text-white' : 'bg-black/20 border-zinc-900 text-zinc-400 hover:border-zinc-805'}`}
              >
                <button
                  type="button"
                  onClick={() => setActivePageId(p.id)}
                  className="font-bold uppercase font-mono text-left block truncate flex-grow cursor-pointer"
                >
                  📄 {p.title}
                </button>
                {project.pages.length > 1 && (
                  <button
                    onClick={() => removePage(p.id, p.title)}
                    className="p-1 hover:text-red-400 text-zinc-500 transition-colors"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>

          <form onSubmit={addNewPage} className="flex gap-1.5">
            <input
              type="text"
              required
              value={newPageTitle}
              onChange={(e) => setNewPageTitle(e.target.value)}
              placeholder="Add Page, e.g. Pricing"
              className="flex-grow bg-black/40 border border-zinc-900 rounded-lg px-2.5 py-1.5 text-[9px] text-white focus:outline-none"
            />
            <button
              type="submit"
              className="p-2 bg-purple-950 border border-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-900 hover:text-white transition-colors cursor-pointer"
            >
              <Plus size={11} />
            </button>
          </form>
        </div>

        {/* COMPONENT SECTIONS EDITOR */}
        <div className="space-y-3 pt-3 border-t border-white/5">
          <div className="flex items-center justify-between text-[10px] font-mono text-zinc-400 uppercase font-bold border-b border-white/5 pb-2">
            <div className="flex items-center gap-1">
              <Layers size={12} className="text-[#00ffff]" />
              <span>Section layout: {activePage.title}</span>
            </div>
          </div>

          <div className="space-y-1 max-h-[160px] overflow-y-auto custom-scrollbar">
            {activePage.layoutSections.map((sec, idx) => (
              <div 
                key={idx} 
                className="p-2 bg-black/40 border border-zinc-900 rounded-xl flex items-center justify-between text-[9px] font-mono group"
              >
                <span className="truncate max-w-[120px] text-zinc-300">▣ {sec}</span>
                <div className="flex items-center gap-1 opacity-60 hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => moveSection(idx, 'UP')}
                    disabled={idx === 0}
                    className="p-0.5 hover:text-[#00ffff] disabled:text-zinc-700 cursor-pointer"
                  >
                    <ChevronUp size={11} />
                  </button>
                  <button
                    onClick={() => moveSection(idx, 'DOWN')}
                    disabled={idx === activePage.layoutSections.length - 1}
                    className="p-0.5 hover:text-[#00ffff] disabled:text-zinc-700 cursor-pointer"
                  >
                    <ChevronDown size={11} />
                  </button>
                  <button
                    onClick={() => removeSection(idx)}
                    className="p-0.5 hover:text-red-400 text-zinc-650 cursor-pointer"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={addCustomSection} className="flex gap-1.5">
            <input
              type="text"
              required
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              placeholder="New Section component, e.g. SLA Ticker"
              className="flex-grow bg-black/40 border border-zinc-900 rounded-lg px-2.5 py-1.5 text-[9px] text-white focus:outline-none"
            />
            <button
              type="submit"
              className="p-2 bg-cyan-950 border border-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-900 hover:text-white transition-colors cursor-pointer"
            >
              <Plus size={11} />
            </button>
          </form>
        </div>

      </div>

    </div>
  );
};
